import {
  Outlet,
  createRootRoute,
  createRoute,
  createRouter,
  redirect,
  useRouterState,
} from '@tanstack/react-router'
import type { SearchSchemaInput } from '@tanstack/react-router'
import { CUSTOMER_STATUSES, LIVE_STATES, MODES, SHIFT_FILTERS } from '@acs/shared'
import type { CustomerStatus, LiveState, Mode, ShiftFilter } from '@acs/shared'
import { AppShell } from '@/components/shell/app-shell'
import {
  ErrorBoundary,
  NotFoundPanel,
  RouteErrorComponent,
} from '@/components/shell/error-boundary'
import { CustomerTable } from '@/components/customers/customer-table'
import { COLUMN_FILTERS, type ColumnFilter } from '@/components/customers/customer-toolbar'
import { CreateCustomerPage } from '@/routes/create-customer'
import { SchedulesPage } from '@/routes/schedules'
import { SettingsPage } from '@/routes/settings'
import { SystemPage } from '@/routes/system'

/**
 * The route tree, written by hand.
 *
 * There is deliberately no generated `routeTree.gen.ts` in the import graph:
 * a codegen step that must run before `tsc` is a build that can fail in a way
 * nobody can reproduce locally. Four routes do not justify that risk, so the
 * tree is ordinary TypeScript and the only thing that can break it is a type
 * error you can see.
 *
 * The other load-bearing idea here is that **search params are the filter
 * state**. There is no `useState` holding the customer list's filters
 * anywhere; the URL holds them. That makes every view a link an operator can
 * paste into chat, makes the back button undo a filter change, and means a
 * refresh lands on exactly the same screen.
 */

/* ------------------------------------------------------------------ *
 * Customer list search schema
 * ------------------------------------------------------------------ */

export const CUSTOMER_SORTS = [
  'created_at',
  'updated_at',
  'name',
  'customer_id',
  /*
   * The two OUTCOME keys.
   *
   * `won_at` is what the Confirmed list opens on — a claim is the reason nearly
   * every row is there, and it is the only ordering that puts the shift somebody
   * just took at the top. Paired with `?won=1` it also turns the Won list into a
   * single ordered index scan; see the note on `CustomerQuery['sort']`.
   *
   * `active_hit_at` was missing from this list and reached the toolbar only
   * because `validateSearch` merges rather than whitelists (see below) — one
   * refactor away from the Active hits link silently opening in `created_at`
   * order. Listed now, so it is validated rather than lucky.
   */
  'won_at',
  'active_hit_at',
] as const
export type CustomerSort = (typeof CUSTOMER_SORTS)[number]

export const SORT_DIRS = ['asc', 'desc'] as const
export type SortDir = (typeof SORT_DIRS)[number]

/**
 * Validated, normalised output for this route's search params.
 *
 * ── What this schema does, and what it does not ─────────────────────
 * It is NOT a whitelist, and it is worth being precise about that because the
 * opposite is easy to assume. Router-core computes a match's search as
 * `{ ...parentSearch, ...validateSearch(parentSearch) }` — a merge. A key this
 * function does not return still reaches `useSearch({ strict: false })`, still
 * survives `navigate()`, and still appears in the URL. Checked against
 * `@tanstack/react-router` 1.171 with a memory history: a schema that knew only
 * `q` still round-tripped `city`, `size` and `computerNumber` intact.
 *
 * So listing a key here buys three things rather than survival:
 *
 *   - It is **normalised and bounded at the edge**. A hand-edited
 *     `?name=<2kB>` is trimmed and cut to `MAX_FILTER_LENGTH` here, instead of
 *     travelling to the API and coming back as a 400 the operator cannot
 *     connect to anything they did.
 *   - It lands in `_strictSearch`, so a typed `useSearch()` on this route sees
 *     it. Only `q`, `mode`, `agent`, `live`, `sort`, `dir` and `page` used to.
 *   - It survives if this router is ever given `search: { strict: true }`.
 *     Under that flag — and only under it — router-core replaces the search
 *     object with this function's output on every navigation, and every key not
 *     listed here really would be deleted. Today `size`, `city`, `jobId` and
 *     `scheduleId` would all vanish the moment somebody set it.
 *
 * The column filters are spread from the same `COLUMN_FILTERS` list the toolbar
 * iterates, so a new column cannot be added to one side only.
 */
export interface CustomerSearch extends Partial<Record<ColumnFilter, string>> {
  q?: string
  mode?: Mode[]
  agent?: string[]
  live?: LiveState[]
  /** The Shift filter — `day` / `night` / `any` / `unset`, any combination. */
  shift?: ShiftFilter[]
  /**
   * Only customers whose shift was claimed. Absent means "no opinion", never
   * "not won" — see the API's own note on the query flag.
   */
  won?: boolean
  /**
   * Only customers who saw a shift go ACTIVE and did not get it. Absent means
   * "no opinion", never "did not miss one" — same rule as `won`, same reason.
   */
  activeHit?: boolean
  jobId?: string
  scheduleId?: string
  sort?: CustomerSort
  dir?: SortDir
  /** 1-based. Absent means page 1, so the default URL stays clean. */
  page?: number
  /** Rows per page. Validated by the toolbar, which owns the allowed set. */
  size?: number
}

/**
 * What a `<Link>` or `navigate()` may hand in — deliberately looser than the
 * output, so callers can pass a raw string from an input box without
 * pre-normalising it.
 */
export interface CustomerSearchInput extends Partial<Record<ColumnFilter, unknown>> {
  q?: string
  mode?: string[] | string
  agent?: string[] | string
  live?: string[] | string
  shift?: string[] | string
  won?: boolean | string
  activeHit?: boolean | string
  jobId?: unknown
  scheduleId?: unknown
  sort?: string
  dir?: string
  page?: number | string
  size?: number | string
}

/* -- coercion helpers ---------------------------------------------- */

/**
 * Accepts everything a URL can realistically carry for a repeatable filter:
 * a real array (TanStack's default JSON search serialiser round-trips these),
 * or a comma-joined string (what a human types by hand).
 */
function toStringArray(value: unknown): string[] {
  if (Array.isArray(value)) {
    return value.flatMap((item) => (typeof item === 'string' && item !== '' ? [item] : []))
  }
  if (typeof value === 'string' && value !== '') {
    return value
      .split(',')
      .map((part) => part.trim())
      .filter((part) => part !== '')
  }
  return []
}

/** Keeps only recognised members, de-duplicated, in the order given. */
function keepKnown<T extends string>(values: string[], allowed: readonly T[]): T[] {
  const allow = new Set<string>(allowed)
  const seen = new Set<string>()
  const out: T[] = []
  for (const value of values) {
    if (!allow.has(value) || seen.has(value)) continue
    seen.add(value)
    out.push(value as T)
  }
  return out
}

function uniqueStrings(values: string[]): string[] {
  return [...new Set(values)]
}

function toEnum<T extends string>(value: unknown, allowed: readonly T[]): T | undefined {
  if (typeof value !== 'string') return undefined
  return (allowed as readonly string[]).includes(value) ? (value as T) : undefined
}

function toPage(value: unknown): number | undefined {
  const n = typeof value === 'number' ? value : typeof value === 'string' ? Number(value) : NaN
  if (!Number.isFinite(n)) return undefined
  const page = Math.floor(n)
  // Page 1 is the default; emitting it would put `?page=1` on every URL.
  return page > 1 ? page : undefined
}

function toSize(value: unknown): number | undefined {
  const n = typeof value === 'number' ? value : typeof value === 'string' ? Number(value) : NaN
  // Membership of `PAGE_SIZES` is the toolbar's rule and stays there; this only
  // has to keep a hand-edited URL from putting something absurd in the params.
  return Number.isFinite(n) && n > 0 ? Math.floor(n) : undefined
}

/**
 * A free-text filter, trimmed and capped.
 *
 * The cap is the one the API's zod schema uses for its widest column filter, so
 * a pasted essay is cut here rather than becoming a 400 the operator cannot
 * connect to anything they did.
 */
const MAX_FILTER_LENGTH = 190

function toFilterText(value: unknown): string | undefined {
  if (typeof value !== 'string') return undefined
  const trimmed = value.trim().slice(0, MAX_FILTER_LENGTH)
  return trimmed === '' ? undefined : trimmed
}

/**
 * Every key is dropped when it carries no information. That is what keeps a
 * shared URL readable and keeps `customersKey()` from producing a different
 * cache entry for `?q=` and no `q` at all.
 */
function validateCustomerSearch(input: CustomerSearchInput & SearchSchemaInput): CustomerSearch {
  const out: CustomerSearch = {}

  const q = typeof input.q === 'string' ? input.q.trim() : ''
  if (q !== '') out.q = q

  const mode = keepKnown(toStringArray(input.mode), MODES)
  if (mode.length > 0) out.mode = mode

  const agent = uniqueStrings(toStringArray(input.agent))
  if (agent.length > 0) out.agent = agent

  const live = keepKnown(toStringArray(input.live), LIVE_STATES)
  if (live.length > 0) out.live = live

  const shift = keepKnown(toStringArray(input.shift), SHIFT_FILTERS)
  if (shift.length > 0) out.shift = shift

  // Only ever set to true, so `?won=0` is not a URL that means anything — the
  // absent key and the false one would otherwise be two spellings of the same
  // filter, and one of them would end up in a bookmark.
  if (input.won === true || input.won === 'true' || input.won === '1') out.won = true
  if (input.activeHit === true || input.activeHit === 'true' || input.activeHit === '1') {
    out.activeHit = true
  }

  const sort = toEnum(input.sort, CUSTOMER_SORTS)
  if (sort !== undefined) out.sort = sort

  const dir = toEnum(input.dir, SORT_DIRS)
  if (dir !== undefined) out.dir = dir

  const page = toPage(input.page)
  if (page !== undefined) out.page = page

  const size = toSize(input.size)
  if (size !== undefined) out.size = size

  const jobId = toFilterText(input.jobId)
  if (jobId !== undefined) out.jobId = jobId

  const scheduleId = toFilterText(input.scheduleId)
  if (scheduleId !== undefined) out.scheduleId = scheduleId

  for (const key of COLUMN_FILTERS) {
    const value = toFilterText(input[key])
    if (value !== undefined) out[key] = value
  }

  return out
}

function isCustomerStatus(value: string): value is CustomerStatus {
  return (CUSTOMER_STATUSES as readonly string[]).includes(value)
}

/* ------------------------------------------------------------------ *
 * Root
 * ------------------------------------------------------------------ */

function RootLayout() {
  // The boundary is keyed on the pathname so that navigating away from a view
  // that threw actually clears it. Without this a single render error turns
  // the whole console into a dead fallback until a full reload.
  const pathname = useRouterState({ select: (s) => s.location.pathname })

  return (
    <AppShell>
      <ErrorBoundary resetKey={pathname}>
        <Outlet />
      </ErrorBoundary>
    </AppShell>
  )
}

const rootRoute = createRootRoute({
  component: RootLayout,
  errorComponent: RouteErrorComponent,
  notFoundComponent: NotFoundPanel,
})

/* ------------------------------------------------------------------ *
 * / -> /customers/approved
 * ------------------------------------------------------------------ */

const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/',
  beforeLoad: () => {
    // `replace` so the empty root never becomes a back-button trap.
    throw redirect({ to: '/customers/$status', params: { status: 'approved' }, replace: true })
  },
})

/* ------------------------------------------------------------------ *
 * /customers/new
 *
 * Registered BEFORE `/customers/$status` for readability only — TanStack ranks
 * a static segment above a dynamic one regardless of declaration order, so
 * `new` can never be parsed as a status. That ranking is asserted in
 * `router.matchRoutes` rather than assumed; if it ever changed, this page would
 * silently become a redirect to the approved list.
 * ------------------------------------------------------------------ */

const createCustomerRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/customers/new',
  component: CreateCustomerPage,
})

/* ------------------------------------------------------------------ *
 * /customers/telegram
 *
 * The Telegram intake's own pending queue. It renders the SAME table as
 * `/customers/pending` — same status, same columns, same row actions — over a
 * different set of rows.
 *
 * Its own pathname rather than `/customers/pending?agent=tg`, for two reasons
 * that both bite in this file's own terms:
 *
 *   - the rail matches links with `includeSearch: false`, so two links to one
 *     pathname would both render as selected — and unlike Approved/Sessions,
 *     where that already happens and the one is a subset of the other, these
 *     two lists are DISJOINT, so two lit rows would assert something false;
 *   - the exclusion becomes a property of the VIEW. There is no param on
 *     `/customers/pending` to delete, so it cannot be edited back into a list
 *     that includes these customers — see `toCustomerQuery`.
 *
 * Registered before `/customers/$status` for readability only; TanStack ranks
 * a static segment above a dynamic one regardless of declaration order, which
 * is the same guarantee `/customers/new` above relies on.
 * ------------------------------------------------------------------ */

function TelegramCustomersRoute() {
  return <CustomerTable status="pending" segment="telegram" />
}

const telegramCustomersRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/customers/telegram',
  validateSearch: validateCustomerSearch,
  component: TelegramCustomersRoute,
})

/* ------------------------------------------------------------------ *
 * /customers/$id/edit
 *
 * The same component as `/customers/new`. An edit form that drifts from the
 * create form is how a field ends up creatable but not correctable — so there
 * is one form, and `customerId` is the only difference between the two modes.
 * ------------------------------------------------------------------ */

function EditCustomerRoute() {
  const { id } = editCustomerRoute.useParams()
  return <CreateCustomerPage customerId={id} />
}

const editCustomerRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/customers/$id/edit',
  component: EditCustomerRoute,
})

/* ------------------------------------------------------------------ *
 * /customers/$status
 * ------------------------------------------------------------------ */

function CustomersRoute() {
  const { status } = customersRoute.useParams()
  // `beforeLoad` has already redirected anything invalid; this narrows the
  // string for the type system without trusting it twice.
  return <CustomerTable status={isCustomerStatus(status) ? status : 'approved'} />
}

const customersRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/customers/$status',
  validateSearch: validateCustomerSearch,
  /**
   * Narrows `$status` for link authors — `<Link params={{ status: 'aproved' }}>`
   * is a compile error rather than a runtime redirect. `parse` deliberately
   * does *not* coerce an unknown value to a default: it hands the raw string
   * through so `beforeLoad` below can see that it is wrong and fix the URL,
   * instead of quietly rendering the approved list at `/customers/garbage`.
   */
  params: {
    parse: (raw: Record<string, string>): { status: CustomerStatus } => ({
      status: (raw['status'] ?? 'approved') as CustomerStatus,
    }),
    stringify: (params: { status: CustomerStatus }): { status: string } => ({
      status: params.status,
    }),
  },
  beforeLoad: ({ params }) => {
    if (!isCustomerStatus(params.status)) {
      throw redirect({ to: '/customers/$status', params: { status: 'approved' }, replace: true })
    }
  },
  component: CustomersRoute,
})

/* ------------------------------------------------------------------ *
 * /schedules
 * ------------------------------------------------------------------ */

const schedulesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/schedules',
  component: SchedulesPage,
})

/* ------------------------------------------------------------------ *
 * /settings
 * ------------------------------------------------------------------ */

const settingsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/settings',
  component: SettingsPage,
})

/* ------------------------------------------------------------------ *
 * /system
 * ------------------------------------------------------------------ */

const systemRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/system',
  component: SystemPage,
})

/*
 * `/sessions/$sessionId` is gone.
 *
 * It deep-linked into a drawer over the sibling fleet's `session_logs`. This
 * console reads its own run log, which is keyed by CUSTOMER rather than by
 * their session id, so there was no honest way to keep the link working.
 */

/* ------------------------------------------------------------------ *
 * Router
 * ------------------------------------------------------------------ */

const routeTree = rootRoute.addChildren([
  indexRoute,
  createCustomerRoute,
  telegramCustomersRoute,
  editCustomerRoute,
  customersRoute,
  schedulesRoute,
  settingsRoute,
  systemRoute,
])

export const router = createRouter({
  routeTree,
  // Prefetch on hover: the rail's three lists are the whole job, and 200ms of
  // hover is enough to make the switch feel instant without prefetching
  // everything on screen.
  defaultPreload: 'intent',
  defaultPreloadStaleTime: 0,
  defaultErrorComponent: RouteErrorComponent,
  defaultNotFoundComponent: NotFoundPanel,
  scrollRestoration: true,
})

export {
  customersRoute,
  telegramCustomersRoute,
  schedulesRoute,
  settingsRoute,
  systemRoute,
  indexRoute,
  rootRoute,
}

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router
  }
}
